/** Optional Blender renders. Geometry and texture dimensions use logical art
 * pixels; PNG pixels are denser by `resolution`, never larger in the world. */
import { ImageSource, Texture } from 'pixi.js';
import { BUILDING_TYPES } from '../data/buildings.ts';
import { VERNACULAR } from './vernacular.ts';
import type { BuildingGeometry } from './textures.ts';

export const BLENDER_BUILDING_IDS = ['house', 'hearth', 'storehouse', 'watchtower'] as const;
export type BlenderBuildingId = string;
export interface BlenderBuildingAsset {
  id: BlenderBuildingId;
  image: string;
  resolution: 4;
  textureWidth: number;
  textureHeight: number;
  geometry: BuildingGeometry;
}
export interface BlenderBuildingManifest {
  version: 1;
  style: string;
  assets: BlenderBuildingAsset[];
}
export interface BlenderBuildingArt { texture: Texture; geometry: BuildingGeometry }
export interface BlenderAssetLoadResult {
  assets: Map<BlenderBuildingId, BlenderBuildingArt>;
  errors: string[];
}
export interface BlenderAssetLoadOptions {
  style?: string;
  manifestUrl?: string;
  timeoutMs?: number;
  /** Injectable I/O keeps the validation, timeout and fallback checks headless. */
  fetch?: typeof fetch;
  loadImage?: (url: string, signal: AbortSignal) => Promise<HTMLImageElement>;
}

const MAX_IMAGE_PIXELS = 4_194_304;
const EPSILON = 0.0001;
const BUILDING_IDS = new Set(BUILDING_TYPES.map(def => def.id));
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

function readHull(raw: unknown, width: number, height: number): Array<[number, number]> | null {
  if (!Array.isArray(raw) || raw.length < 3 || raw.length > 128) return null;
  const points: Array<[number, number]> = [];
  for (const p of raw) {
    if (!Array.isArray(p) || p.length !== 2 || !finite(p[0]) || !finite(p[1]) ||
      p[0] < 0 || p[1] < 0 || p[0] > width || p[1] > height) return null;
    points.push([p[0], p[1]]);
  }
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    area += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(area) > EPSILON ? points : null;
}

/** Pure, all-or-nothing schema validation. A malformed manifest retains the
 * generated set; a valid manifest can contain any subset of the game catalog. */
export function validateBlenderManifest(raw: unknown): BlenderBuildingManifest | null {
  if (!record(raw) || raw.version !== 1 || typeof raw.style !== 'string' ||
    !Object.hasOwn(VERNACULAR, raw.style) || !Array.isArray(raw.assets) ||
    raw.assets.length > BUILDING_IDS.size) return null;
  const assets: BlenderBuildingAsset[] = [];
  const seen = new Set<string>();
  for (const item of raw.assets) {
    if (!record(item) || typeof item.id !== 'string' || !BUILDING_IDS.has(item.id) || seen.has(item.id)) return null;
    if (typeof item.image !== 'string' || !/^(?:[\w-]+\/)*[\w-]+\.png$/i.test(item.image) || item.resolution !== 4) return null;
    const width = item.textureWidth, height = item.textureHeight;
    if (!finite(width) || !finite(height) || width <= 0 || height <= 0 ||
      !Number.isInteger(width * 4) || !Number.isInteger(height * 4) || width * 4 > 4096 || height * 4 > 4096 ||
      width * height * 16 > MAX_IMAGE_PIXELS) return null;
    const g = item.geometry;
    if (!record(g) || !record(g.bounds) || !finite(g.groundCenterX) || !finite(g.groundFrontY)) return null;
    const silhouette = readHull(g.silhouette, width, height), groundFootprint = readHull(g.groundFootprint, width, height);
    if (!silhouette || !groundFootprint) return null;
    const { x0, y0, x1, y1 } = g.bounds;
    if (!finite(x0) || !finite(y0) || !finite(x1) || !finite(y1) ||
      x0 < 0 || y0 < 0 || x1 > width || y1 > height || x1 <= x0 || y1 <= y0) return null;
    for (const [x, y] of [...silhouette, ...groundFootprint]) {
      if (x < x0 - EPSILON || x > x1 + EPSILON || y < y0 - EPSILON || y > y1 + EPSILON) return null;
    }
    if (g.groundCenterX < Math.min(...groundFootprint.map(p => p[0])) ||
      g.groundCenterX > Math.max(...groundFootprint.map(p => p[0])) ||
      Math.abs(g.groundFrontY - Math.max(...groundFootprint.map(p => p[1]))) > EPSILON) return null;
    let chimney: [number, number] | undefined;
    if (g.chimney !== undefined) {
      const point = g.chimney;
      if (!Array.isArray(point) || point.length !== 2 || !finite(point[0]) || !finite(point[1]) ||
        point[0] < x0 || point[0] > x1 || point[1] < y0 || point[1] > y1) return null;
      chimney = [point[0], point[1]];
    }
    assets.push({
      id: item.id as BlenderBuildingId, image: item.image, resolution: 4,
      textureWidth: width, textureHeight: height,
      geometry: { silhouette, groundFootprint, groundCenterX: g.groundCenterX, groundFrontY: g.groundFrontY, bounds: { x0, y0, x1, y1 }, ...(chimney ? { chimney } : {}) },
    });
    seen.add(item.id);
  }
  return { version: 1, style: raw.style, assets };
}

/** Vite may serve from /, a hosted subdirectory, or a relative desktop base. */
export function blenderManifestUrl(baseUrl: string, documentUrl: string, style = 'ashen'): string {
  if (!Object.hasOwn(VERNACULAR, style)) throw new Error('Unknown building architecture.');
  return new URL(`art/blender/${style === 'ashen' ? '' : style + '/'}manifest.json`, new URL(baseUrl, documentUrl)).href;
}

export function blenderImageMatches(asset: BlenderBuildingAsset, pixelWidth: number, pixelHeight: number): boolean {
  return pixelWidth === asset.textureWidth * asset.resolution && pixelHeight === asset.textureHeight * asset.resolution;
}

/** Do not pass explicit width/height to ImageSource: it infers physical pixels
 * from the decoded image and divides them by resolution exactly once. */
export function blenderBuildingTexture(asset: BlenderBuildingAsset, image: HTMLImageElement, style = 'ashen'): Texture | null {
  if (!blenderImageMatches(asset, image.naturalWidth, image.naturalHeight)) return null;
  return new Texture({
    source: new ImageSource({ resource: image, resolution: asset.resolution, scaleMode: 'linear', label: `blender:${style}:${asset.id}` }),
    label: `blender:${style}:${asset.id}`,
  });
}

function loadImage(url: string, signal: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new Error('Building asset load timed out.')); return; }
    const image = new Image();
    const cleanup = () => { image.onload = null; image.onerror = null; signal.removeEventListener('abort', abort); };
    const abort = () => { cleanup(); image.removeAttribute('src'); reject(new Error('Building asset load timed out.')); };
    image.onload = () => { cleanup(); resolve(image); };
    image.onerror = () => { cleanup(); reject(new Error('Building image could not be loaded.')); };
    signal.addEventListener('abort', abort, { once: true });
    image.crossOrigin = 'anonymous';
    image.src = url;
  });
}

/** One bounded boot load. Missing/invalid assets are a rendering fallback, never
 * a reason to stop play. No Pixi global asset cache or extra canvas copy is used. */
export async function loadBlenderBuildingAssets(options: BlenderAssetLoadOptions = {}): Promise<BlenderAssetLoadResult> {
  const result: BlenderAssetLoadResult = { assets: new Map(), errors: [] };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), Math.max(1, options.timeoutMs ?? 12000));
  try {
    const style = options.style ?? 'ashen';
    const url = options.manifestUrl ?? blenderManifestUrl(import.meta.env.BASE_URL, document.baseURI, style);
    const response = await (options.fetch ?? globalThis.fetch)(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`Building manifest unavailable (${response.status}).`);
    const text = await response.text();
    if (text.length > 4_194_304) throw new Error('Building manifest is too large.');
    const manifest = validateBlenderManifest(JSON.parse(text));
    if (!manifest || manifest.style !== style) throw new Error('Building manifest is invalid for this architecture.');
    await Promise.all(manifest.assets.map(async asset => {
      try {
        const image = await (options.loadImage ?? loadImage)(new URL(asset.image, url).href, controller.signal);
        if (controller.signal.aborted) return;
        const texture = blenderBuildingTexture(asset, image, style);
        if (!texture) throw new Error('Decoded image dimensions do not match its logical size and resolution.');
        result.assets.set(asset.id, { texture, geometry: asset.geometry });
      } catch (error) {
        result.errors.push(`${asset.id}: ${error instanceof Error ? error.message : 'Image load failed.'}`);
      }
    }));
  } catch (error) {
    result.errors.push(error instanceof Error ? error.message : 'Building assets unavailable.');
  } finally {
    clearTimeout(timer);
  }
  return result;
}
